import React, { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Clover, ArrowRight, RefreshCw, Mail } from 'lucide-react';
import { toast } from 'sonner';

const inputStyle = {
    width: '48px',
    height: '56px',
    textAlign: 'center',
    fontSize: '24px',
    fontWeight: '800',
    color: '#064E3B',
    backgroundColor: '#ffffff',
    border: 'none',
    borderBottom: '2px solid #cbd5e1',
    outline: 'none',
    caretColor: '#064E3B',
    WebkitTextFillColor: '#064E3B',
};

const inputFilledStyle = {
    ...inputStyle,
    borderBottom: '2px solid #064E3B',
};

const VerifyEmail = () => {
    const [code, setCode] = useState(['', '', '', '', '', '']);
    const [loading, setLoading] = useState(false);
    const [resending, setResending] = useState(false);
    const inputs = useRef([]);
    const navigate = useNavigate();
    const location = useLocation();
    const { loginWithToken } = useAuth();

    const email = location.state?.email || '';

    const handleChange = (index, e) => {
        const raw = e.target.value;
        // Take only the last character typed
        const char = raw.replace(/\D/g, '').slice(-1);
        const newCode = [...code];
        newCode[index] = char;
        setCode(newCode);
        if (char && index < 5) {
            setTimeout(() => inputs.current[index + 1]?.focus(), 10);
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace') {
            if (code[index]) {
                const newCode = [...code];
                newCode[index] = '';
                setCode(newCode);
            } else if (index > 0) {
                inputs.current[index - 1]?.focus();
            }
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        if (pasted.length === 6) {
            setCode(pasted.split(''));
            setTimeout(() => inputs.current[5]?.focus(), 10);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const fullCode = code.join('');
        if (fullCode.length !== 6) {
            toast.error('Introduce el código completo de 6 dígitos');
            return;
        }
        if (!email) {
            toast.error('Email no encontrado. Vuelve a registrarte.');
            navigate('/register');
            return;
        }
        setLoading(true);
        try {
            const response = await authAPI.verifyEmail(email, fullCode);
            const { access_token, user } = response.data;
            loginWithToken(access_token, user);
            toast.success('¡Email verificado! Bienvenido a TREBOL Finance 🍀');
            navigate('/dashboard');
        } catch (error) {
            const msg = error.response?.data?.detail || 'Código incorrecto o expirado';
            toast.error(msg);
            setCode(['', '', '', '', '', '']);
            setTimeout(() => inputs.current[0]?.focus(), 10);
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (!email) {
            toast.error('Email no encontrado. Vuelve a registrarte.');
            navigate('/register');
            return;
        }
        setResending(true);
        try {
            await authAPI.resendVerification(email);
            toast.success('Nuevo código enviado. Revisa tu bandeja de entrada.');
            setCode(['', '', '', '', '', '']);
            setTimeout(() => inputs.current[0]?.focus(), 10);
        } catch (error) {
            toast.error('Error al reenviar. Inténtalo de nuevo.');
        } finally {
            setResending(false);
        }
    };

    return (
        <div className="min-h-screen flex" data-testid="verify-email-page">
            <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 lg:px-16 xl:px-24 bg-white py-12">
                <div className="max-w-md w-full mx-auto">
                    {/* Logo */}
                    <div className="flex items-center gap-3 mb-10">
                        <div className="w-10 h-10 bg-emerald-900 rounded-sm flex items-center justify-center">
                            <Clover className="w-6 h-6 text-white" strokeWidth={1.5} />
                        </div>
                        <span className="font-display font-bold text-xl tracking-tight text-slate-900">
                            Trebol Finance by Leapifyes
                        </span>
                    </div>

                    {/* Header */}
                    <div className="mb-8">
                        <div className="w-14 h-14 bg-emerald-50 rounded-full flex items-center justify-center mb-6">
                            <Mail className="w-7 h-7 text-emerald-900" strokeWidth={1.5} />
                        </div>
                        <h1 className="font-display font-bold text-3xl text-slate-900 tracking-tight mb-2">
                            Verifica tu email
                        </h1>
                        <p className="text-slate-500 text-sm leading-relaxed">
                            Hemos enviado un código de 6 dígitos a{' '}
                            <span className="font-semibold text-slate-700">{email || 'tu email'}</span>.
                            Introdúcelo para activar tu cuenta.
                        </p>
                    </div>

                    {/* Code inputs */}
                    <form onSubmit={handleSubmit} className="space-y-8">
                        <div className="flex gap-3 justify-center">
                            {code.map((digit, index) => (
                                <input
                                    key={index}
                                    ref={(el) => (inputs.current[index] = el)}
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    maxLength={1}
                                    value={digit}
                                    onChange={(e) => handleChange(index, e)}
                                    onKeyDown={(e) => handleKeyDown(index, e)}
                                    onPaste={index === 0 ? handlePaste : undefined}
                                    style={digit ? inputFilledStyle : inputStyle}
                                    data-testid={`code-input-${index}`}
                                    autoComplete="off"
                                />
                            ))}
                        </div>

                        <Button
                            type="submit"
                            disabled={loading || code.join('').length !== 6}
                            className="w-full bg-emerald-900 hover:bg-emerald-800 text-white rounded-sm uppercase tracking-widest text-xs font-bold py-6 transition-colors"
                            data-testid="verify-submit-btn"
                        >
                            {loading ? 'Verificando...' : 'Activar cuenta'}
                            <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                    </form>

                    {/* Resend */}
                    <div className="mt-8 text-center">
                        <p className="text-sm text-slate-500 mb-3">¿No recibiste el código?</p>
                        <button
                            type="button"
                            onClick={handleResend}
                            disabled={resending}
                            className="inline-flex items-center gap-2 text-emerald-900 font-semibold text-sm hover:underline disabled:opacity-50"
                            data-testid="resend-code-btn"
                        >
                            <RefreshCw className={`w-4 h-4 ${resending ? 'animate-spin' : ''}`} />
                            {resending ? 'Enviando...' : 'Reenviar código'}
                        </button>
                    </div>

                    {/* Back to register */}
                    <p className="mt-6 text-center text-sm text-slate-400">
                        ¿Email incorrecto?{' '}
                        <button
                            type="button"
                            onClick={() => navigate('/register')}
                            className="text-emerald-900 font-semibold hover:underline"
                        >
                            Volver al registro
                        </button>
                    </p>
                </div>
            </div>

            {/* Right side */}
            <div
                className="hidden lg:block lg:w-1/2 relative"
                style={{
                    backgroundImage: 'url(https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1200&q=80)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }}
            >
                <div className="absolute inset-0 bg-emerald-900/80" />
                <div className="absolute inset-0 flex flex-col justify-center items-center p-12 text-center">
                    <Clover className="w-16 h-16 text-white/80 mb-6" strokeWidth={1} />
                    <h2 className="text-white text-2xl font-display font-bold mb-4">
                        Un paso más
                    </h2>
                    <p className="text-white/70 text-sm max-w-xs leading-relaxed">
                        La verificación de email protege tu cuenta y garantiza que solo tú puedas acceder a tus datos financieros.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default VerifyEmail;
